import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Dispatch } from 'redux';
import { GIButton } from 'app-components';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';

import Dropdown from '../../../lib/inputs/Dropdown';
import Checkbox from '../../../components/common/form/Checkbox';
import Tooltip from '../../../components/common/tooltip/Tooltip';
import { INPUT_TYPES } from '../../../components/common/form/constants/fieldTypes';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import AlertMessage from '../../../components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { FormFields } from './types/AdminProvision.interface';
import { addNewAdminPrivileges } from '../../../actions/admin/adminProvisionActionCreators';
import { useAppSelector, useAppDispatch } from '../../../hooks';
import useAdminProvision from './hooks/useAdminProvision';

const FormContainer = styled.div`
    margin: 15px auto;
    padding: 10px 0;
    align-items: left;

    .float-r-m-b {
        float: right;
        margin-bottom: 10px;
    }

    .gi-alert {
        padding: 1rem;
    }

    .gi-dropdown__select {
        width: 250px;
    }

    .gi-dropdown__label {
        width: 200px;
    }

    .required {
        font-size: 1.8rem;
        font-weight: bold;
    }
`;

const StyledContainer = styled.div`
    padding-left: 200px;
`;

const StyledCheckbox = styled.div`
    display: flex;
    align-items: center;

    .ga-tooltip-container {
        padding-bottom: 10px;
        padding-left: 5px;
    }
`;

const StyledDiv = styled.div``;

const StyledH1 = styled.h1`
    p {
        display: inline;
        font-weight: 600;
    }
`;

const AdminProvisionForm = () => {
    const intl = useIntl();
    const dispatch: Dispatch<any> = useAppDispatch();
    const { hasButtonDisabled, appyTableFilter } = useAdminProvision();
    const hasError = useAppSelector(state => state.adminProvision.hasError);
    const { provisioningRoleList, managedRoleList, provisioningRuleList } = useAppSelector(
        state => state.adminProvision.privileges,
    );

    const { register, handleSubmit, setValue, watch, reset } = useForm<FormFields>();
    const values = watch();

    const { onChange, onBlur } = register('hasAllowCreate');
    const { onChange: onChangeAllowUpdate, onBlur: onBlurAllowUpdate } = register('hasAllowUpdate');

    useEffect(() => {
        appyTableFilter(values.provisioningRole, values.managedRole);
    }, [values.provisioningRole, values.managedRole, provisioningRoleList]);

    const handleProvisionRoleChange = (name, value: string) => {
        setValue(name, value);
    };

    const handleManagedRoleChange = (name, value: string) => {
        setValue(name, value);
    };

    const onSubmit = data => {
        dispatch(
            addNewAdminPrivileges({
                provisioningRoleLabel: data.provisioningRole,
                managedRoleLabel: data.managedRole,
                hasAllowCreate: data.hasAllowCreate,
                hasAllowUpdate: data.hasAllowUpdate,
                provisioningRoleList,
                managedRoleList,
            }),
        );
    };

    const onReset = () => {
        reset();
        appyTableFilter(values.provisioningRole, values.managedRole, true);
    };

    return (
        <FormContainer data-testid="admin-provision-form-container">
            <StyledH1 className="p-inline" data-testid="admin-provision-form-h1">
                <FormattedMessage
                    id="admin.provision.header"
                    values={{
                        totalRules: provisioningRuleList?.length,
                        para: function makeParaTag(chunks) {
                            return <p>{chunks}</p>;
                        },
                    }}
                />
            </StyledH1>
            <ConditionalRendering isTrue={hasError}>
                <AlertMessage
                    className="margin-bottom-2"
                    showIcon={false}
                    content={<FormattedMessage id="admin.provision.failedInfo" />}
                    type="error"
                />
            </ConditionalRendering>
            <form onSubmit={handleSubmit(onSubmit)} id="add-privileges" data-testid="admin-provision-form">
                <StyledDiv data-testid="admin-provision-form-dropdown-container">
                    <Dropdown
                        label={{
                            text: <FormattedMessage id="admin.provision.provisioningRole" />,
                            labelClass: [''],
                        }}
                        inputData={{
                            id: 'select-provisioning-role',
                            inputClass: [''],
                            name: 'provisioningRole',
                            options: [
                                {
                                    value: intl.formatMessage({ id: 'admin.provision.dropdownSelectOne' }),
                                    text: intl.formatMessage({ id: 'admin.provision.dropdownSelectOne' }),
                                },
                                ...provisioningRoleList.map(role => ({ value: role.label, text: role.label })),
                            ],
                            type: INPUT_TYPES.DROPDOWN,
                            value: values.provisioningRole,
                            isInputRequired: false,
                            ...register('provisioningRole'),
                        }}
                        inputActions={{
                            handleFieldChange: val => handleProvisionRoleChange('provisioningRole', val),
                        }}
                    />

                    <Dropdown
                        label={{
                            text: <FormattedMessage id="admin.provision.managedRole" />,
                            labelClass: [''],
                        }}
                        inputData={{
                            id: 'select-managed-role',
                            inputClass: [''],
                            name: 'managedRole',
                            options: [
                                {
                                    value: intl.formatMessage({ id: 'admin.provision.dropdownSelectOne' }),
                                    text: intl.formatMessage({ id: 'admin.provision.dropdownSelectOne' }),
                                },
                                ...managedRoleList.map(role => ({ value: role.label, text: role.label })),
                            ],
                            type: INPUT_TYPES.DROPDOWN,
                            value: values.managedRole,
                            isInputRequired: false,
                            ...register('managedRole'),
                        }}
                        inputActions={{
                            handleFieldChange: val => handleManagedRoleChange('managedRole', val),
                        }}
                    />
                    <StyledContainer data-testid="admin-provision-form-checkbox-container">
                        <StyledCheckbox data-testid="admin-provision-tooltip-container">
                            <Checkbox
                                id="allowCreate"
                                isInputRequired={true}
                                name="allowCreate"
                                text="admin.provision.table.column.allowCreate"
                                handleChange={onChange}
                                handleBlur={onBlur}
                                checked={values.hasAllowCreate}
                                ariaHidden={true}
                                showRequired={false}
                                {...register('hasAllowCreate')}
                            />
                            <Tooltip
                                id={<FontAwesomeIcon className="text-color" icon={faQuestionCircle} />}
                                elementId="allowCreateTooltip"
                                dataAutomationId="allowCreateTooltip"
                                index="1"
                                content="admin.provision.tooltipAllowCreate"
                                place="top"
                            ></Tooltip>
                        </StyledCheckbox>

                        <StyledCheckbox data-testid="admin-provision-tooltip-container2">
                            <Checkbox
                                id="allowUpdate"
                                isInputRequired={true}
                                name="allowUpdate"
                                text="admin.provision.table.column.allowUpdate"
                                handleChange={onChangeAllowUpdate}
                                handleBlur={onBlurAllowUpdate}
                                checked={values.hasAllowUpdate}
                                ariaHidden={true}
                                showRequired={false}
                                {...register('hasAllowUpdate')}
                            />
                            <Tooltip
                                id={<FontAwesomeIcon className="text-color " icon={faQuestionCircle} />}
                                elementId="allowUpdateTooltip"
                                dataAutomationId="allowUpdateTooltip"
                                index="2"
                                content="admin.provision.tooltipAllowUpdate"
                                place="top"
                            ></Tooltip>
                        </StyledCheckbox>
                        <GIButton
                            id="add_btn"
                            dataTestId="add_btn"
                            labelId="ticket_management.custom_field.add_btn"
                            isDisabled={hasButtonDisabled(values.provisioningRole, values.managedRole)}
                            type="submit"
                        />
                    </StyledContainer>
                </StyledDiv>

                <AlertMessage
                    className="margin-top-4 margin-bottom-2"
                    showIcon={false}
                    content={
                        <FormattedMessage
                            id="admin.provision.showInfo"
                            values={{
                                asterisk: '*',
                                span: function makeSpanTag(chunks) {
                                    return <span className="required">{chunks}</span>;
                                },
                            }}
                        />
                    }
                    type="info"
                />
                <GIButton
                    id="show_all_rules_btn"
                    dataTestId="show_all_rules_btn"
                    labelId="admin.provision.showAllRules"
                    type="reset"
                    addlClassNames="float-r-m-b"
                    isDisabled={false}
                    onClickHandler={onReset}
                />
            </form>
        </FormContainer>
    );
};

export default AdminProvisionForm;
